import * as Sentry from "@sentry/react"
import { api } from "@stoneflow/backend/convex/_generated/api"
import type { Id } from "@stoneflow/backend/convex/_generated/dataModel"
import {
	PRIORITIES,
	type Priority,
	STATUSES,
	type Status,
} from "@stoneflow/shared/validators/status"
import { createFileRoute } from "@tanstack/react-router"
import { useMutation, useQuery } from "convex/react"
import { useEffect, useRef, useState } from "react"
import { toast } from "sonner"
import { ActivityLog } from "#/components/activity-log"
import { AppLink } from "#/components/app-link"
import { AttachmentList } from "#/components/attachment-list"
import { AttachmentUploader } from "#/components/attachment-uploader"
import { CommentThread } from "#/components/comment-thread"
import type { FieldDefinition } from "#/components/dynamic-field"
import { FieldValueDisplay } from "#/components/field-value-display"
import { PriorityBadge } from "#/components/priority-badge"
import { RouteError } from "#/components/route-error"
import { ExceptionDetailSkeleton } from "#/components/skeletons/exception-detail-skeleton"
import { StatusBadge } from "#/components/status-badge"
import { Button } from "#/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "#/components/ui/card"
import { Label } from "#/components/ui/label"
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "#/components/ui/select"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "#/components/ui/tabs"
import { useAppNavigate } from "#/hooks/use-app-navigate"
import { useLogRole } from "#/hooks/useLogRole"
import { useOrgMembers, withYouSuffix } from "#/hooks/useOrgMembers"
import { useFeatureFlag } from "#/lib/feature-flags"
import { segmentByGridLayout } from "#/lib/field-layout"
import { trackEvent } from "#/lib/posthog"
import { useStonelog } from "#/lib/stonelog-context"
import { formatRelTime } from "#/lib/utils"

export const Route = createFileRoute("/_authenticated/stonelog/exceptions/$exceptionId")({
	errorComponent: RouteError,
	component: ExceptionDetail,
})

const GROUP_LABELS: Record<string, string> = {
	core: "Core Details",
	order_info: "Order Information",
	supplier_info: "Supplier Information",
	part_info: "Part Information",
}

function getGroupLabel(g: string): string {
	return GROUP_LABELS[g] ?? g.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
}

function isStatus(value: string): value is Status {
	const known: readonly string[] = STATUSES
	return known.includes(value)
}

function isPriority(value: string): value is Priority {
	const known: readonly string[] = PRIORITIES
	return known.includes(value)
}

function toStatus(value: string): Status {
	return isStatus(value) ? value : "new"
}

function toPriority(value: string): Priority {
	return isPriority(value) ? value : "medium"
}

function ExceptionDetail() {
	const { exceptionId } = Route.useParams()
	const exId = exceptionId as Id<"exceptions">
	const navigate = useAppNavigate()
	const { currentLogId } = useStonelog()

	// All editing/commenting on an exception is editor+ on its log.
	const { atLeast } = useLogRole(currentLogId ?? undefined)
	const canEdit = atLeast("editor")
	const canAssign = canEdit
	const canComment = canEdit
	const canAttach = canEdit
	const canReopenClosed = useFeatureFlag("stonelog.reopen-closed")
	const { members, currentUserId } = useOrgMembers()

	const exception = useQuery(api.stonelog.exceptions.get, { exceptionId: exId })
	const parent = useQuery(
		api.stonelog.exceptions.get,
		exception?.parentId ? { exceptionId: exception.parentId } : "skip",
	)
	const children = useQuery(api.stonelog.exceptions.listChildren, { exceptionId: exId })
	const comments = useQuery(api.stonelog.comments.list, { exceptionId: exId })
	const events = useQuery(api.stonelog.events.list, { exceptionId: exId })
	const attachments = useQuery(api.stonelog.attachments.list, { exceptionId: exId })
	const fieldSchema = useQuery(
		api.stonelog.fieldSchemas.getVersion,
		exception?.logTypeId && exception?.schemaVersion
			? { logTypeId: exception.logTypeId, version: exception.schemaVersion }
			: "skip",
	)
	const category = useQuery(
		api.stonelog.categories.get,
		exception?.categoryId ? { categoryId: exception.categoryId } : "skip",
	)
	const exceptionType = useQuery(
		api.stonelog.exceptionTypes.get,
		exception?.typeId ? { typeId: exception.typeId } : "skip",
	)
	const departments = useQuery(
		api.stonelog.departments.listForLog,
		currentLogId ? { logId: currentLogId } : "skip",
	)

	const markOpen = useMutation(api.stonelog.exceptions.markOpen)
	const updateStatus = useMutation(api.stonelog.exceptions.updateStatus)
	const assign = useMutation(api.stonelog.exceptions.assign)

	const [selectedDeptId, setSelectedDeptId] = useState<string>("")
	const [selectedUserId, setSelectedUserId] = useState<string>("")
	const [isAssigning, setIsAssigning] = useState(false)
	const [isClosing, setIsClosing] = useState(false)
	const [isTransitioning, setIsTransitioning] = useState(false)
	const markOpenCalled = useRef(false)

	useEffect(() => {
		if (exception?.status === "new" && !markOpenCalled.current) {
			markOpenCalled.current = true
			markOpen({ exceptionId: exId }).catch(() => {})
		}
	}, [exception?.status, exId, markOpen])

	useEffect(() => {
		if (exception?.assignedDepartmentId) {
			setSelectedDeptId(exception.assignedDepartmentId)
		}
	}, [exception?.assignedDepartmentId])

	useEffect(() => {
		if (exception?.assignedUserId) {
			setSelectedUserId(exception.assignedUserId)
		}
	}, [exception?.assignedUserId])

	async function handleClose() {
		if (!exception) return
		setIsClosing(true)
		try {
			await updateStatus({ exceptionId: exId, status: "closed" })
			trackEvent("stonelog_exception_status_updated", {
				exception_id: exId,
				from_status: exception.status,
				to_status: "closed",
			})
			toast.success("Exception closed")
		} catch (err) {
			Sentry.captureException(err)
			toast.error(err instanceof Error ? err.message : "Failed to close exception")
		} finally {
			setIsClosing(false)
		}
	}

	const STATUS_TRANSITIONS: Record<string, { status: string; label: string }[]> = {
		open: [
			{ status: "in_progress", label: "Mark In Progress" },
			{ status: "resolved", label: "Mark Resolved" },
		],
		in_progress: [{ status: "resolved", label: "Mark Resolved" }],
		resolved: [{ status: "in_progress", label: "Mark In Progress" }],
		closed: canReopenClosed ? [{ status: "in_progress", label: "Reopen" }] : [],
	}

	async function handleTransition(newStatus: string) {
		if (!exception) return
		setIsTransitioning(true)
		try {
			await updateStatus({ exceptionId: exId, status: newStatus })
			trackEvent("stonelog_exception_status_updated", {
				exception_id: exId,
				from_status: exception.status,
				to_status: newStatus,
			})
			toast.success("Status updated")
		} catch (err) {
			Sentry.captureException(err)
			toast.error(err instanceof Error ? err.message : "Failed to update status")
		} finally {
			setIsTransitioning(false)
		}
	}

	async function handleReassign() {
		if (!selectedDeptId && !selectedUserId) return
		setIsAssigning(true)
		try {
			await assign({
				exceptionId: exId,
				assignedDepartmentId: selectedDeptId ? (selectedDeptId as Id<"departments">) : undefined,
				assignedUserId: selectedUserId || undefined,
			})
			trackEvent("stonelog_exception_assigned", {
				exception_id: exId,
				department_id: selectedDeptId || undefined,
				user_id: selectedUserId || undefined,
			})
			toast.success("Reassigned")
		} catch (err) {
			Sentry.captureException(err)
			toast.error(err instanceof Error ? err.message : "Failed to reassign")
		} finally {
			setIsAssigning(false)
		}
	}

	if (exception === undefined) {
		return <ExceptionDetailSkeleton />
	}

	if (exception === null) {
		return (
			<div className="flex flex-col items-center justify-center gap-3 p-12">
				<p className="text-sm text-foreground-secondary">Exception not found</p>
				<Button
					variant="outline"
					size="sm"
					onClick={() => navigate({ to: "/stonelog/exceptions" })}
				>
					Back to Exceptions
				</Button>
			</div>
		)
	}

	const fields = (fieldSchema?.fields ?? []) as FieldDefinition[]

	const groupMap = new Map<string, Array<{ field: FieldDefinition; value: unknown }>>()
	for (const f of fields) {
		const val = exception.dynamicFields?.[f.id]
		if (val == null || val === "") continue
		const g = f.group ?? "core"
		if (!groupMap.has(g)) groupMap.set(g, [])
		groupMap.get(g)?.push({ field: f, value: val })
	}
	const sortedGroups = [...groupMap.entries()].sort(([, aItems], [, bItems]) => {
		const aOrder = Math.min(...aItems.map((i) => i.field.order ?? 99))
		const bOrder = Math.min(...bItems.map((i) => i.field.order ?? 99))
		return aOrder - bOrder
	})

	const isClosed = exception.status === "closed"
	const assignedUserName = exception.assignedUserId
		? withYouSuffix(
				members.find((m) => m.userId === exception.assignedUserId)?.name ??
					exception.assignedUserId,
				exception.assignedUserId,
				currentUserId,
			)
		: null

	return (
		<div className="mx-auto max-w-6xl p-6">
			<div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
				<div className="lg:col-span-2 space-y-6">
					<div className="space-y-2">
						<div className="flex flex-wrap items-center gap-3">
							<code className="font-mono text-2xl font-semibold tracking-tight">
								{exception.reference}
							</code>
							<PriorityBadge priority={toPriority(exception.priority)} />
							<StatusBadge status={toStatus(exception.status)} />
						</div>
						<p className="text-sm text-foreground-muted">
							Raised by{" "}
							{withYouSuffix(
								exception.raisedByName ??
									members.find((m) => m.userId === exception.raisedByUserId)?.name ??
									"Unknown",
								exception.raisedByUserId,
								currentUserId,
							)}{" "}
							· {formatRelTime(exception._creationTime)}
						</p>
						{exception.schemaVersion && (
							<span className="inline-block rounded border border-border px-1.5 py-0.5 text-xs text-foreground-muted">
								Schema v{exception.schemaVersion}
							</span>
						)}
					</div>

					<Card>
						<CardHeader>
							<CardTitle>Details</CardTitle>
						</CardHeader>
						<CardContent className="space-y-6">
							<div className="space-y-1">
								<p className="text-technical">Category & Type</p>
								<p className="text-sm">
									{category?.name ?? "—"} / {exceptionType?.name ?? "—"}
								</p>
							</div>
							{sortedGroups.length === 0 ? (
								<p className="text-sm text-foreground-muted">No field data.</p>
							) : (
								sortedGroups.map(([group, items]) => (
									<div key={group} className="space-y-3">
										<p className="text-technical">{getGroupLabel(group)}</p>
										{segmentByGridLayout(
											items
												.sort((a, b) => (a.field.order ?? 99) - (b.field.order ?? 99))
												.map((i) => ({ ...i, width: i.field.width })),
										).map((segment) => (
											<div
												key={segment.items[0].field.id}
												className={
													segment.cols === 3 ? "grid grid-cols-3 gap-4" : "grid grid-cols-2 gap-4"
												}
											>
												{segment.items.map(({ field, value }) => (
													<div
														key={field.id}
														className={
															segment.cols === 3
																? "col-span-1"
																: field.width === "full" || !field.width
																	? "col-span-2"
																	: "col-span-1"
														}
													>
														<FieldValueDisplay field={field} value={value} />
													</div>
												))}
											</div>
										))}
									</div>
								))
							)}
						</CardContent>
					</Card>

					<Card>
						<CardHeader>
							<CardTitle>Communications & Activity</CardTitle>
						</CardHeader>
						<CardContent>
							<Tabs defaultValue="comments">
								<TabsList variant="line">
									<TabsTrigger value="comments">Comments</TabsTrigger>
									<TabsTrigger value="attachments">
										Attachments
										{attachments && attachments.length > 0 ? ` (${attachments.length})` : ""}
									</TabsTrigger>
									<TabsTrigger value="activity">Activity</TabsTrigger>
								</TabsList>
								<TabsContent value="comments" className="mt-4">
									<CommentThread
										comments={comments ?? []}
										exceptionId={exId}
										canComment={canComment}
									/>
								</TabsContent>
								<TabsContent value="attachments" className="mt-4">
									<div className="flex flex-col gap-4">
										{canAttach && !isClosed && (
											<AttachmentUploader exceptionId={exId} disabled={isClosed} />
										)}
										<AttachmentList attachments={attachments ?? []} />
									</div>
								</TabsContent>
								<TabsContent value="activity" className="mt-4">
									<ActivityLog
										events={(events ?? []).map((e) => ({
											_id: e._id,
											eventType: e.eventType,
											payload: e.payload as Record<string, unknown>,
											_creationTime: e._creationTime,
										}))}
									/>
								</TabsContent>
							</Tabs>
						</CardContent>
					</Card>
				</div>

				<div className="space-y-4">
					<Card>
						<CardHeader>
							<CardTitle>Status</CardTitle>
						</CardHeader>
						<CardContent className="space-y-3">
							<StatusBadge status={toStatus(exception.status)} className="text-base" />
							{canEdit &&
								(STATUS_TRANSITIONS[exception.status] ?? []).map((t) => (
									<Button
										key={t.status}
										variant="outline"
										size="sm"
										className="w-full"
										onClick={() => handleTransition(t.status)}
										disabled={isTransitioning}
									>
										{t.label}
									</Button>
								))}
							{canEdit && !isClosed && (
								<Button
									variant="outline"
									size="sm"
									className="w-full"
									onClick={handleClose}
									disabled={isClosing}
								>
									{isClosing ? "Closing..." : "Close Exception"}
								</Button>
							)}
						</CardContent>
					</Card>

					<Card>
						<CardHeader>
							<CardTitle>Relationships</CardTitle>
						</CardHeader>
						<CardContent className="space-y-3">
							<div>
								<p className="text-technical mb-1">Parent</p>
								{exception.parentId ? (
									<AppLink
										to="/stonelog/exceptions/$exceptionId"
										params={{ exceptionId: exception.parentId }}
										className="inline-flex items-center gap-2 text-sm hover:underline"
									>
										<code className="font-mono font-medium">{parent?.reference ?? "…"}</code>
										{parent?.description && (
											<span className="truncate text-foreground-muted">{parent.description}</span>
										)}
									</AppLink>
								) : (
									<p className="text-sm text-foreground-muted">None</p>
								)}
							</div>
							<div>
								<div className="flex items-center justify-between mb-1">
									<p className="text-technical">Children</p>
									{canEdit && !exception.parentId && (
										<AppLink
											to="/stonelog/exceptions/new"
											search={{ parentId: exId }}
											className="text-xs text-foreground-secondary hover:underline"
										>
											+ New child
										</AppLink>
									)}
								</div>
								{children === undefined ? (
									<p className="text-sm text-foreground-muted">Loading…</p>
								) : children.length === 0 ? (
									<p className="text-sm text-foreground-muted">None</p>
								) : (
									<ul className="space-y-1">
										{children.map((c) => (
											<li key={c._id}>
												<AppLink
													to="/stonelog/exceptions/$exceptionId"
													params={{ exceptionId: c._id }}
													className="inline-flex items-center gap-2 text-sm hover:underline"
												>
													<code className="font-mono font-medium">{c.reference}</code>
													<StatusBadge status={toStatus(c.status)} />
												</AppLink>
											</li>
										))}
									</ul>
								)}
								{exception.parentId && (
									<p className="mt-2 text-xs text-foreground-muted">
										Child exceptions cannot have their own children.
									</p>
								)}
							</div>
						</CardContent>
					</Card>

					<Card>
						<CardHeader>
							<CardTitle>Assignment</CardTitle>
						</CardHeader>
						<CardContent className="space-y-3">
							<div>
								<p className="text-technical mb-1">Current</p>
								<p className="text-sm">
									Department:{" "}
									{exception.assignedDepartmentId
										? (departments?.find((d) => d._id === exception.assignedDepartmentId)?.name ??
											exception.assignedDepartmentId)
										: "Unassigned"}
								</p>
								{assignedUserName && (
									<p className="text-sm text-foreground-secondary mt-0.5">
										User: {assignedUserName}
									</p>
								)}
							</div>
							{canAssign && (
								<>
									{departments && departments.length > 0 && (
										<div className="space-y-1">
											<Label className="text-technical">Reassign to Department</Label>
											<Select value={selectedDeptId} onValueChange={setSelectedDeptId}>
												<SelectTrigger>
													<SelectValue placeholder="Select department" aria-label="Department">
														{departments.find((d) => d._id === selectedDeptId)?.name}
													</SelectValue>
												</SelectTrigger>
												<SelectContent>
													{departments.map((d) => (
														<SelectItem key={d._id} value={d._id}>
															{d.name}
														</SelectItem>
													))}
												</SelectContent>
											</Select>
										</div>
									)}
									{members.length > 0 && (
										<div className="space-y-1">
											<Label className="text-technical">Assign to User</Label>
											<Select value={selectedUserId} onValueChange={setSelectedUserId}>
												<SelectTrigger>
													<SelectValue placeholder="Select user" aria-label="User">
														{selectedUserId
															? withYouSuffix(
																	members.find((m) => m.userId === selectedUserId)?.name ??
																		"Unassigned",
																	selectedUserId,
																	currentUserId,
																)
															: undefined}
													</SelectValue>
												</SelectTrigger>
												<SelectContent>
													<SelectItem value="">Unassigned</SelectItem>
													{members.map((m) => (
														<SelectItem key={m.userId} value={m.userId}>
															{withYouSuffix(m.name, m.userId, currentUserId)}
														</SelectItem>
													))}
												</SelectContent>
											</Select>
										</div>
									)}
									<Button
										size="sm"
										className="w-full"
										onClick={handleReassign}
										disabled={(!selectedDeptId && !selectedUserId) || isAssigning}
									>
										{isAssigning ? "Reassigning..." : "Reassign"}
									</Button>
								</>
							)}
						</CardContent>
					</Card>
				</div>
			</div>
		</div>
	)
}
